import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { FolderOpen, FileText, Download, Package, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public auditor view. No consultant login required.
 *
 * States:
 *   loading  – peeking at the link
 *   pinent   – link is valid, waiting for PIN
 *   ready    – PIN accepted, showing documents
 *   invalid  – 404 / 410 (revoked or expired)
 */
export default function AuditorShare() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/share/public/${token}`, { withCredentials: true })
      .then((r) => { setMeta(r.data); setState("pinent"); loadDocs(); })
      .catch((e) => {
        setError(e.response?.data?.detail || "Link unavailable");
        setState("invalid");
      });
    // `loadDocs` intentionally excluded — it references `token` via closure
    // and re-invocation would loop the initial peek. This effect only
    // needs to fire once per token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadDocs() {
    try {
      const r = await axios.get(`${API}/share/public/${token}/documents`, { withCredentials: true });
      setData(r.data);
      setState("ready");
    } catch (err) {
      // 401 is expected until PIN is verified — anything else worth surfacing.
      if (err.response?.status && err.response.status !== 401) {
        console.warn("Auditor docs fetch failed", err.response?.status);
      }
    }
  }

  async function verify(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/share/public/${token}/verify`, { pin: pin.trim() }, { withCredentials: true });
      await loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "PIN check failed");
    }
    setSubmitting(false);
  }

  async function downloadDoc(d) {
    try {
      const r = await axios.get(`${API}/share/public/${token}/documents/${d.id}/download`,
        { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  }

  async function downloadZip() {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/share/public/${token}/zip`,
        { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "document_suite.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Bulk download failed"); }
    setBusy(false);
  }

  const grouped = useMemo(() => {
    if (!data?.documents) return {};
    const g = {};
    for (const d of data.documents) {
      const k = d.folder || "01 - Core Policies";
      g[k] = g[k] || [];
      g[k].push(d);
    }
    return g;
  }, [data]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center text-sm text-slate-500">Loading…</div>
    );
  }
  if (state === "invalid") {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
            <h1 className="text-2xl font-semibold text-[#0D1B2A] mb-2">Link unavailable</h1>
            <p className="text-sm text-slate-500">{error}. Please contact your Next Provider consultant.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="auditor-share-page">
      <header className="bg-[#0D1B2A] text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#1A7F6E] mb-1">Next Provider Australia</p>
            <h1 className="text-xl font-semibold">Auditor document review</h1>
          </div>
          <span className="text-xs text-slate-300">nextprovider.com.au</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Client</p>
            <h2 className="text-2xl font-semibold text-[#0D1B2A]" data-testid="auditor-org-name">{meta?.org_name || "—"}</h2>
            {meta?.expires_at && (
              <p className="text-xs text-slate-500 mt-1">Access expires {new Date(meta.expires_at).toLocaleDateString()}</p>
            )}
          </CardContent>
        </Card>

        {state === "pinent" && (
          <Card>
            <CardContent className="p-6">
              <div className="max-w-sm mx-auto text-center">
                <Lock className="w-10 h-10 mx-auto text-[#1A7F6E] mb-3" />
                <h3 className="text-lg font-semibold text-[#0D1B2A]">Enter access PIN</h3>
                <p className="text-xs text-slate-500 mb-4">The Next Provider consultant will have shared a PIN with you separately.</p>
                <form onSubmit={verify} className="space-y-3">
                  <Label htmlFor="pin" className="sr-only">PIN</Label>
                  <Input id="pin" autoFocus value={pin} onChange={(e) => setPin(e.target.value)}
                         type="password" inputMode="numeric" placeholder="••••" className="text-center tracking-widest" data-testid="pin-input" />
                  <Button type="submit" className="w-full bg-[#1A7F6E] hover:bg-[#136053] text-white" disabled={submitting || pin.length < 4} data-testid="pin-submit">
                    {submitting ? "Verifying…" : "Access documents"}
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        )}

        {state === "ready" && data && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#0D1B2A]">
                <b>{data.documents.length}</b> document{data.documents.length === 1 ? "" : "s"} available
              </p>
              <Button size="sm" variant="outline" onClick={downloadZip} disabled={busy} data-testid="auditor-zip-btn">
                <Package className="w-3.5 h-3.5 mr-1.5" />Download all (.zip)
              </Button>
            </div>
            {Object.keys(grouped).sort().map((folder) => (
              <Card key={folder} data-testid={`auditor-folder-${folder}`}>
                <CardContent className="p-0">
                  <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-[#1A7F6E]" />
                    <p className="text-sm font-semibold text-[#0D1B2A]">{folder}</p>
                    <Badge variant="outline" className="ml-auto text-xs">{grouped[folder].length}</Badge>
                  </div>
                  <ul>
                    {grouped[folder].map((d) => (
                      <li key={d.id} className="p-3 border-b last:border-b-0 border-slate-100 flex items-center gap-3" data-testid={`auditor-doc-${d.id}`}>
                        <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0D1B2A] truncate">{d.template_name}</p>
                          <p className="text-[11px] text-slate-500">{d.version} · {(d.size_bytes / 1024).toFixed(0)} KB</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} data-testid={`auditor-dl-${d.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        <p className="text-center text-xs text-slate-400 pt-6 border-t border-slate-200">
          Documents prepared by Next Provider Australia — nextprovider.com.au
        </p>
      </main>
    </div>
  );
}
